const GB_KEY  = process.env.REACT_APP_GOOGLE_BOOKS_KEY;
const GB_BASE = 'https://www.googleapis.com/books/v1/volumes';
const RECOMMEND_URL = process.env.REACT_APP_RANK_FUNCTION_URL || '/.netlify/functions/rank-books';

function cleanDescription(html) {
  if (!html) return null;
  return html
    .replace(/<br\s*\/?>/gi, '\n\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim() || null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 1 — Generate candidates via Claude
// The serverless function returns Claude's literary judgment directly:
// [{ title, author, matchReason }]
// ─────────────────────────────────────────────────────────────────────────────
async function generateCandidates(anchorBooks, selectedVibes, category, shownBooks) {
  let res;
  try {
    res = await fetch(RECOMMEND_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        anchorBooks: anchorBooks.map(b => ({ title: b.title, author: b.author })),
        vibes: selectedVibes,
        category,
        shownBooks,
      }),
    });
  } catch (networkErr) {
    console.error('[Matchbook] network error calling rank-books:', networkErr);
    throw networkErr;
  }

  console.log('[Matchbook] rank-books status:', res.status);
  if (!res.ok) {
    const body = await res.text();
    console.error('[Matchbook] rank-books error body:', body);
    throw new Error(`Recommendation service returned ${res.status}`);
  }

  let parsed;
  try {
    parsed = await res.json();
  } catch (parseErr) {
    console.error('[Matchbook] rank-books JSON parse error:', parseErr);
    throw parseErr;
  }

  const { books } = parsed;
  console.log('[Matchbook] rank-books returned', books?.length, 'candidates');
  if (!Array.isArray(books)) throw new Error('Malformed recommendations response');
  // An empty array is a valid "no matches" result, not an error — let it flow
  // through so the UI shows the empty state instead of "can't load recommendations".
  return books;
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 2 — Verify each candidate and fetch real metadata
// Claude is reliable but can misremember titles or invent books. Every book
// must verify against Google Books or Open Library before reaching the user.
// ─────────────────────────────────────────────────────────────────────────────

function normTitle(t) {
  return (t ?? '')
    .toLowerCase()
    .replace(/^(the |a |an )/i, '')   // strip leading articles
    .replace(/\s*:.*$/, '')            // strip subtitles
    .replace(/[^a-z0-9\s]/g, '')      // strip punctuation
    .trim();
}

function titlesMatch(returned, target) {
  const a = normTitle(returned), b = normTitle(target);
  if (!a || !b) return false;
  if (a === b) return true;
  // Allow substring match only when the shorter title is ≥70% of the longer.
  // This lets "Feathered Serpent" match its full subtitle'd version while
  // blocking "Vegetarian" from matching "Vegetarian Myth" (different book).
  const shorter = a.length <= b.length ? a : b;
  const longer  = a.length <= b.length ? b : a;
  return longer.includes(shorter) && shorter.length / longer.length >= 0.70;
}

function authorsMatch(returned, target) {
  if (!returned || !target) return false;
  const norm = s => s.toLowerCase().replace(/[^a-z\s]/g, '').trim();
  const a = norm(returned), b = norm(target);
  const lastName = s => s.split(/\s+/).filter(Boolean).pop() ?? '';
  return a === b || lastName(a) === lastName(b) || a.includes(b) || b.includes(a);
}

function parseGBItem(item) {
  if (!item?.volumeInfo) return null;
  const info = item.volumeInfo;
  const cover = info.imageLinks?.thumbnail
    ?.replace('http:', 'https:')
    ?.replace('zoom=1', 'zoom=2') ?? null;
  const rawCategory = info.categories?.[0] ?? null;

  // Log raw description once to verify what Google Books actually sends
  if (info.description) {
    console.log('[Matchbook] raw GB description for', info.title, '→', JSON.stringify(info.description.slice(0, 400)));
  }

  return {
    key:          `gb:${item.id}`,
    gbId:         item.id,
    title:        info.title ?? 'Unknown',
    author:       (info.authors ?? [])[0] ?? 'Unknown author',
    year:         info.publishedDate?.slice(0, 4) ?? null,
    description:  cleanDescription(info.description),
    categories:   info.categories ?? [],
    bookCategory: rawCategory?.replace(/\s*\/\s*.+/, '').replace(/\s*&\s*.+/, '').trim() ?? null,
    publisher:  info.publisher ?? null,
    pageCount:  info.pageCount ?? null,
    coverUrl:   cover,
    source:     'googlebooks',
  };
}

async function verifyViaGoogleBooks(title, author) {
  try {
    const params = new URLSearchParams({
      q: `intitle:"${title}" inauthor:"${author}"`,
      maxResults: '3',
      printType: 'books',
      ...(GB_KEY ? { key: GB_KEY } : {}),
    });
    const res = await fetch(`${GB_BASE}?${params}`);
    if (!res.ok) return null;
    const data = await res.json();

    // Find the first result where title and author both roughly match.
    for (const item of data.items ?? []) {
      const parsed = parseGBItem(item);
      if (!parsed) continue;
      if (titlesMatch(parsed.title, title) && authorsMatch(parsed.author, author)) {
        return parsed;
      }
    }
    return null;
  } catch {
    return null;
  }
}

async function verifyViaOpenLibrary(title, author) {
  try {
    const params = new URLSearchParams({ title, author, limit: '3', fields: 'title,author_name,cover_i,first_publish_year,number_of_pages_median,key' });
    const res = await fetch(`https://openlibrary.org/search.json?${params}`);
    if (!res.ok) return null;
    const data = await res.json();

    for (const doc of data.docs ?? []) {
      const returnedAuthor = doc.author_name?.[0] ?? '';
      if (!titlesMatch(doc.title, title) || !authorsMatch(returnedAuthor, author)) continue;

      const coverId = doc.cover_i;
      return {
        key:          `ol:${doc.key}`,
        title:        doc.title,
        author:       returnedAuthor || author,
        year:         doc.first_publish_year?.toString() ?? null,
        description:  null,   // OL descriptions require a separate work fetch; skip for MVP
        categories:   [],
        bookCategory: null,
        publisher:    null,
        pageCount:    doc.number_of_pages_median ?? null,
        coverUrl:     coverId ? `https://covers.openlibrary.org/b/id/${coverId}-L.jpg` : null,
        source:       'openlibrary',
      };
    }
    return null;
  } catch {
    return null;
  }
}

async function fetchOLCover(title, author) {
  try {
    const params = new URLSearchParams({ title, author, limit: '3', fields: 'title,author_name,cover_i' });
    const res = await fetch(`https://openlibrary.org/search.json?${params}`);
    if (!res.ok) return null;
    const data = await res.json();
    for (const doc of data.docs ?? []) {
      if (!titlesMatch(doc.title, title)) continue;
      if (doc.cover_i) return `https://covers.openlibrary.org/b/id/${doc.cover_i}-L.jpg`;
    }
    return null;
  } catch {
    return null;
  }
}

async function verifyCandidate({ title, author, matchReason }) {
  const book = (await verifyViaGoogleBooks(title, author))
            ?? (await verifyViaOpenLibrary(title, author));

  if (!book) {
    console.warn(`[Matchbook] unverified: "${title}" by ${author}`);
    return null;
  }

  // If the book verified but has no cover, try OL as a cover-only fallback
  const coverUrl = book.coverUrl ?? (await fetchOLCover(title, author));

  return { ...book, coverUrl, matchReason };
}

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────
// Safety net: under hard requirements Claude occasionally lists a non-qualifying
// book with a matchReason that admits it doesn't fit ("...does not qualify",
// "cannot confirm...") instead of omitting it. Drop those before the user sees them.
const DISQUALIFYING_REASON = /does\s*n['o]?t\s+(?:qualify|exist|meet\s+(?:the\s+)?\w+\s+requirement)|\bnot\s+qualif(?:y|ied)\b|(?:cannot|could\s+not|can'?t)\s+confirm|disqualif/i;

export async function fetchRecommendations({ anchorBooks = [], selectedVibes = [], category = null, shownBooks = [] }) {
  const candidates = (await generateCandidates(anchorBooks, selectedVibes, category, shownBooks))
    .filter(c => !DISQUALIFYING_REASON.test(c.matchReason || ''));

  const results = await Promise.allSettled(candidates.map(verifyCandidate));

  const excludeSet = new Set(shownBooks.map(s => s.toLowerCase()));
  return results
    .filter(r => r.status === 'fulfilled' && r.value !== null)
    .map(r => r.value)
    .filter(b => !excludeSet.has(`"${b.title.toLowerCase()}" by ${b.author.toLowerCase()}`));
}
